import React, { useState, useEffect } from 'react';
import { QuillEditor } from '../Editor/QuillEditor';
import { settingsService, LandingContent } from '../../services/settings';
import { Eye, EyeOff } from 'lucide-react';
import { MenuManagement } from './MenuManagement';
import { FooterManagement } from './FooterManagement';

const defaultContent: LandingContent = {
  title: 'Bienvenue chez CQO',
  subtitle: 'Retrouvez ci-dessous nos formations dédiées à nos applications',
  backgroundImage: '',
  bannerImages: [
    'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1170&h=450&fit=crop',
    'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1170&h=450&fit=crop',
    'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1170&h=450&fit=crop',
    'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1170&h=450&fit=crop'
  ],
  menuItems: [
    {
      id: '1',
      label: 'A propos de CQO',
      url: 'https://www.bycqo.com',
      target: '_blank'
    },
    {
      id: '2',
      label: 'Contactez nous',
      url: 'https://www.bycqo.com/contact',
      target: '_blank'
    }
  ],
  footer: {
    address: '12 rue du 35ème régiment d\'aviation - 69500, Bron',
    phone: '+33 (0)4 27 11 85 26',
    mobile: '06 08 61 40 91',
    email: 'formalog@cqo-dentaire.fr'
  },
  section1: {
    title: 'Section 1',
    imageUrl: '',
    content: '',
    trainingUrl: ''
  },
  section2: {
    title: 'Section 2',
    imageUrl: '',
    content: '',
    trainingUrl: ''
  },
  section3: {
    title: 'Section 3',
    imageUrl: '',
    content: '',
    trainingUrl: ''
  },
  section4: {
    title: 'Section 4',
    imageUrl: '',
    content: '',
    trainingUrl: ''
  },
  useFormasaasStyle: false,
  visibleSections: {
    menu: true,
    header: true,
    footer: true,
    backgroundImage: true,
    banner: true,
    section1: true,
    section2: true,
    section3: true,
    section4: true,
    search: true
  }
};

const SectionEditor: React.FC<{
  sectionNumber: number;
  section: {
    title: string;
    imageUrl: string;
    content: string;
    trainingUrl: string;
  };
  onChange: (section: any) => void;
  modules: any;
}> = ({ sectionNumber, section, onChange, modules }) => (
  <div className="space-y-4">
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Titre de la section
      </label>
      <input
        type="text"
        value={section.title}
        onChange={(e) => onChange({ ...section, title: e.target.value })}
        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        placeholder="Titre de la section"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        URL de l'image
      </label>
      <div className="flex gap-4">
        <input
          type="url"
          value={section.imageUrl}
          onChange={(e) => onChange({ ...section, imageUrl: e.target.value })}
          className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="https://example.com/image.jpg"
        />
        {section.imageUrl && (
          <div className="w-40 h-24 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
            <img
              src={section.imageUrl}
              alt={`Aperçu Section ${sectionNumber}`}
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).src = 'https://via.placeholder.com/160x96?text=Image+non+valide';
              }}
            />
          </div>
        )}
      </div>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        URL de la formation
      </label>
      <input
        type="url"
        value={section.trainingUrl}
        onChange={(e) => onChange({ ...section, trainingUrl: e.target.value })}
        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        placeholder="https://example.com/formation"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Contenu
      </label>
      <QuillEditor
        value={section.content}
        onChange={(value) => onChange({ ...section, content: value })}
        modules={modules}
        className="bg-white dark:bg-gray-700 [&_.ql-editor]:min-h-[200px]"
      />
    </div>
  </div>
);

export const LandingPageEditor: React.FC = () => {
  const [content, setContent] = useState<LandingContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const data = await settingsService.getLandingContent();
      setContent(data);
      setError(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      setContent(defaultContent);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!content) return;

    setSaveStatus('saving');
    setError(null);
    try {
      console.log('Saving landing content...', content);
      await settingsService.saveLandingContent(content);
      console.log('Landing content saved successfully');
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err: unknown) {
      console.error('Error saving landing content:', err);
      const message = err instanceof Error ? err.message : String(err);
      setError(`Erreur lors de la sauvegarde: ${message}`);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  const modules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'header': [1, 2, 3, false] }],
      [{ 'color': [] }],
      ['link'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }]
    ]
  };

  const VisibilityToggle: React.FC<{
    label: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
  }> = ({ label, checked, onChange }) => (
    <label className="flex items-center gap-2 cursor-pointer">
      <div className="relative">
        <input
          type="checkbox"
          className="sr-only"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <div className={`block w-14 h-8 rounded-full transition-colors ${
          checked ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
        }`}>
          <div className={`absolute left-1 top-1 bg-white dark:bg-gray-200 w-6 h-6 rounded-full transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-0'
          }`}></div>
        </div>
      </div>
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
        {checked ? <Eye className="w-4 h-4 inline mr-2" /> : <EyeOff className="w-4 h-4 inline mr-2" />}
        {label}
      </span>
    </label>
  );

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
        Contenu de la page d'accueil
      </h2>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Mode FormaSaas Toggle */}
      <div className="bg-gradient-to-r from-[#004B87] to-[#009FDA] rounded-lg p-6 mb-8">
        <div className="flex items-center justify-between">
          <div className="text-white">
            <h3 className="text-lg font-semibold mb-1">Mode Page d'accueil FormaSaas</h3>
            <p className="text-sm text-blue-100">
              Activez ce mode pour utiliser la page d'accueil harmonisée avec le style FormaSaas (logo, boutons, pattern hexagonal)
            </p>
          </div>
          <label className="flex items-center cursor-pointer">
            <div className="relative">
              <input
                type="checkbox"
                className="sr-only"
                checked={content?.useFormasaasStyle ?? false}
                onChange={(e) => setContent(prev => prev ? ({ ...prev, useFormasaasStyle: e.target.checked }) : null)}
              />
              <div className={`block w-14 h-8 rounded-full transition-colors ${
                content?.useFormasaasStyle ? 'bg-[#F39200]' : 'bg-white/30'
              }`}>
                <div className={`absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform shadow-md ${
                  content?.useFormasaasStyle ? 'translate-x-6' : 'translate-x-0'
                }`}></div>
              </div>
            </div>
            <span className="ml-3 text-white font-medium">
              {content?.useFormasaasStyle ? 'Activé' : 'Désactivé'}
            </span>
          </label>
        </div>
      </div>

      {/* Configuration manuelle (visible uniquement si mode FormaSaas désactivé) */}
      {!content?.useFormasaasStyle && (
      <div className="space-y-8">
        {/* Menu Management */}
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Menu de navigation</h3>
            <VisibilityToggle
              label="Visible"
              checked={content?.visibleSections.menu ?? true}
              onChange={(checked) => setContent(prev => prev ? ({
                ...prev,
                visibleSections: { ...prev.visibleSections, menu: checked }
              }) : null)}
            />
          </div>
          <MenuManagement
            menuItems={content?.menuItems || []}
            onChange={(menuItems) => setContent(prev => prev ? { ...prev, menuItems } : null)}
          />
        </div>

        {/* Footer Management */}
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Pied de page</h3>
            <VisibilityToggle
              label="Visible"
              checked={content?.visibleSections.footer ?? true}
              onChange={(checked) => setContent(prev => prev ? ({
                ...prev,
                visibleSections: { ...prev.visibleSections, footer: checked }
              }) : null)}
            />
          </div>
          <FooterManagement
            footer={content?.footer || defaultContent.footer}
            onChange={(footer) => setContent(prev => prev ? { ...prev, footer } : null)}
          />
        </div>

        {/* Background Image */}
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Image de fond</h3>
            <VisibilityToggle
              label="Visible"
              checked={content?.visibleSections.backgroundImage ?? true}
              onChange={(checked) => setContent(prev => prev ? ({
                ...prev,
                visibleSections: { ...prev.visibleSections, backgroundImage: checked }
              }) : null)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              URL de l'image de fond
            </label>
            <div className="flex gap-4">
              <input
                type="url"
                value={content?.backgroundImage}
                onChange={(e) => setContent(prev => prev ? ({ ...prev, backgroundImage: e.target.value }) : null)}
                className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="https://example.com/background.jpg"
              />
              {content?.backgroundImage && (
                <div className="w-40 h-24 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
                  <img
                    src={content.backgroundImage}
                    alt="Aperçu de l'image de fond"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://via.placeholder.com/160x96?text=Image+non+valide';
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Title and Subtitle */}
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">En-tête de la page</h3>
            <VisibilityToggle
              label="Visible"
              checked={content?.visibleSections.header ?? true}
              onChange={(checked) => setContent(prev => prev ? ({
                ...prev,
                visibleSections: { ...prev.visibleSections, header: checked }
              }) : null)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Titre principal
            </label>
            <input
              type="text"
              value={content?.title}
              onChange={(e) => setContent(prev => prev ? ({ ...prev, title: e.target.value }) : null)}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Titre de la page d'accueil"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Sous-titre
            </label>
            <input
              type="text"
              value={content?.subtitle}
              onChange={(e) => setContent(prev => prev ? ({ ...prev, subtitle: e.target.value }) : null)}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Sous-titre de la page d'accueil"
            />
          </div>
        </div>

        {/* Banner Images */}
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Images de la bannière</h3>
            <VisibilityToggle
              label="Visible"
              checked={content?.visibleSections.banner ?? true}
              onChange={(checked) => setContent(prev => prev ? ({
                ...prev,
                visibleSections: { ...prev.visibleSections, banner: checked }
              }) : null)}
            />
          </div>

          <div className="grid gap-4">
            {content?.bannerImages.map((url, index) => (
              <div key={index} className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Image {index + 1}
                </label>
                <div className="flex gap-4">
                  <input
                    type="url"
                    value={url}
                    onChange={(e) => {
                      const newBannerImages = [...(content?.bannerImages || [])];
                      newBannerImages[index] = e.target.value;
                      setContent(prev => prev ? ({ ...prev, bannerImages: newBannerImages as [string, string, string, string] }) : null);
                    }}
                    className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="https://example.com/image.jpg"
                  />
                  {url && (
                    <div className="w-40 h-24 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
                      <img
                        src={url}
                        alt={`Aperçu ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://via.placeholder.com/160x96?text=Image+non+valide';
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Section 1 */}
        {content && (
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Section 1</h3>
              <VisibilityToggle
                label="Visible"
                checked={content?.visibleSections.section1 ?? true}
                onChange={(checked) => setContent(prev => prev ? ({
                  ...prev,
                  visibleSections: { ...prev.visibleSections, section1: checked }
                }) : null)}
              />
            </div>
            <SectionEditor
              sectionNumber={1}
              section={content.section1}
              onChange={(section) => setContent(prev => prev ? ({ ...prev, section1: section }) : null)}
              modules={modules}
            />
          </div>
        )}

        {/* Section 2 */}
        {content && (
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Section 2</h3>
              <VisibilityToggle
                label="Visible"
                checked={content?.visibleSections.section2 ?? true}
                onChange={(checked) => setContent(prev => prev ? ({
                  ...prev,
                  visibleSections: { ...prev.visibleSections, section2: checked }
                }) : null)}
              />
            </div>
            <SectionEditor
              sectionNumber={2}
              section={content.section2}
              onChange={(section) => setContent(prev => prev ? ({ ...prev, section2: section }) : null)}
              modules={modules}
            />
          </div>
        )}

        {/* Section 3 */}
        {content && (
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Section 3</h3>
              <VisibilityToggle
                label="Visible"
                checked={content?.visibleSections.section3 ?? true}
                onChange={(checked) => setContent(prev => prev ? ({
                  ...prev,
                  visibleSections: { ...prev.visibleSections, section3: checked }
                }) : null)}
              />
            </div>
            <SectionEditor
              sectionNumber={3}
              section={content.section3}
              onChange={(section) => setContent(prev => prev ? ({ ...prev, section3: section }) : null)}
              modules={modules}
            />
          </div>
        )}

        {/* Section 4 */}
        {content && (
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Section 4</h3>
              <VisibilityToggle
                label="Visible"
                checked={content?.visibleSections.section4 ?? true}
                onChange={(checked) => setContent(prev => prev ? ({
                  ...prev,
                  visibleSections: { ...prev.visibleSections, section4: checked }
                }) : null)}
              />
            </div>
            <SectionEditor
              sectionNumber={4}
              section={content.section4}
              onChange={(section) => setContent(prev => prev ? ({ ...prev, section4: section }) : null)}
              modules={modules}
            />
          </div>
        )}
      </div>
      )}

      <div className="flex justify-end mt-8">
        <button
          onClick={handleSave}
          disabled={saveStatus === 'saving'}
          className="px-4 py-2 text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
        >
          {saveStatus === 'saving' && (
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
          )}
          {saveStatus === 'saving' ? 'Enregistrement...' :
           saveStatus === 'saved' ? 'Enregistré !' :
           'Enregistrer les modifications'}
        </button>
      </div>
    </div>
  );
};